import React from "react";
import { cn } from "@/lib/utils";

/** Pixels to move per arrow key press */
const KEYBOARD_STEP = 10;

export interface ResizeHandleProps {
  /** Whether the handle is currently being dragged */
  isDragging?: boolean;
  /** Mouse down handler to initiate the resize */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Called when the user presses an arrow key; delta is in pixels (negative = narrower, positive = wider). */
  onKeyboardResize?: (delta: number) => void;
  /** Current panel width in pixels (exposed as aria-valuenow for screen readers) */
  ariaValueNow: number;
  /** Minimum allowed panel width in pixels */
  ariaValueMin: number;
  /** Maximum allowed panel width in pixels */
  ariaValueMax: number;
  /** Additional class names */
  className?: string;
}

/**
 * A vertical resize handle that sits between two panels.
 *
 * Visual design:
 * - Thin (4px visible) line that expands its hit area (12px) for easy grabbing
 * - Shows a subtle highlight on hover and a stronger highlight when dragging
 * - Displays a centered grip indicator (dots) on hover for discoverability
 * - Uses col-resize cursor to indicate resize affordance
 *
 * Accessibility:
 * - Has role="separator" with aria-orientation="vertical" per WAI-ARIA
 * - Tabindex for keyboard discoverability (visual only — drag requires mouse)
 */
const ResizeHandle = ({
  isDragging = false,
  onMouseDown,
  onKeyboardResize,
  ariaValueNow,
  ariaValueMin,
  ariaValueMax,
  className,
}: ResizeHandleProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!onKeyboardResize) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      onKeyboardResize(-KEYBOARD_STEP);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      onKeyboardResize(KEYBOARD_STEP);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panels"
      aria-valuenow={ariaValueNow}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      tabIndex={0}
      onMouseDown={onMouseDown}
      onKeyDown={handleKeyDown}
      className={cn(
        // Layout: zero-width in flow, with a wide hit area via padding
        "group relative flex-shrink-0 w-0 cursor-col-resize select-none",
        // Hit area: 12px wide centered on the visual line
        "before:absolute before:inset-y-0 before:-left-[6px] before:w-[12px] before:content-['']",
        // Visual line: 2px centered
        "after:absolute after:inset-y-0 after:left-[-1px] after:w-[2px] after:content-['']",
        "after:bg-border after:transition-colors after:duration-150",
        // Hover state
        "hover:after:bg-blue-400 dark:hover:after:bg-blue-500",
        // Active/dragging state
        isDragging &&
          "after:bg-blue-500 dark:after:bg-blue-400 after:w-[3px] after:left-[-1.5px]",
        className,
      )}
    >
      {/* Grip indicator — visible on hover */}
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 -left-[4px] w-[8px] flex flex-col items-center gap-[3px] py-2",
          "opacity-0 transition-opacity duration-150",
          "group-hover:opacity-100",
          isDragging ? "opacity-100" : "hover:opacity-100",
        )}
        aria-hidden="true"
      >
        <div className="w-[4px] h-[4px] rounded-full bg-muted-foreground/40" />
        <div className="w-[4px] h-[4px] rounded-full bg-muted-foreground/40" />
        <div className="w-[4px] h-[4px] rounded-full bg-muted-foreground/40" />
      </div>
    </div>
  );
};

export { ResizeHandle };
